import { Download as DownloadIcon } from '@creditornot/cb-icons';
import { MenuActionItem } from '@creditornot/cb-menu';
import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { getProductSalesInvoice } from 'apis/invoicing-service/api';
import { getProductSalesInvoiceForVenue } from 'apis/restaurant-api/merchant-admin/payouts/api';
import { Product, productNameMapping } from 'apis/restaurant-api/merchant-admin/payouts/types';
import { APIErrorNotification } from 'common/components';
import { useFetch } from 'common/hooks';
import { convertMonetaryIntToDecimal } from 'common/utils/currencyDecimals';
import { downloadFileFromAxiosResponse } from 'common/utils/download';
import { ResponsiveTable } from 'modules/experience-core';

import LocalizedMonetaryAmount from './LocalizedMonetaryAmount';

const ActionsContainer = styled.div`
  min-width: 120px;
  padding: 8px;
  border-radius: 8px;
  overflow: hidden;
`;

const ActionsMenuItem = styled(MenuActionItem).attrs({ variant: 'blue', icon: <DownloadIcon /> })`
  margin-block-start: 8px;
  margin-block-end: 8px;
`;

const InvoiceList = ({ venueId }: { venueId: string }) => {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });

  const { data = [], error } = useFetch(() => getProductSalesInvoiceForVenue(venueId), [venueId]);

  const handleProductSaleInvoiceDownload = async (invoiceId?: string) => {
    const pdfUrl = await getProductSalesInvoice(invoiceId);
    downloadFileFromAxiosResponse(pdfUrl);
  };

  const productSalesData = useMemo(() => [...data].filter(({ deduction_type }) => deduction_type === 'fixed'), [data]);

  return (
    <div>
      {error && <APIErrorNotification error={error} />}
      {data && (
        <ResponsiveTable<Product>
          dataSource={productSalesData}
          getRowKey={record => record.id.$oid}
          columns={[
            {
              id: '1',
              name: t('payout-reports.product_name'),
              render: ({ record }) => productNameMapping[record.product_type],
            },
            {
              id: '2',
              name: t('payout-reports.status'),
              render: ({ record }) => (record.settled ? 'Settled' : 'Ongoing'),
            },
            {
              id: '3',
              name: t('payout-reports.details'),
              render: ({ record }) => record.note,
            },
            {
              id: '4',
              name: t('payout-reports.total_amount_vat_inc'),
              render: ({ record }) => {
                return (
                  <LocalizedMonetaryAmount
                    amount={convertMonetaryIntToDecimal(record.amount, record.currency)}
                    currency={record.currency}
                  />
                );
              },
            },
            {
              id: '5',
              name: t('payout-reports.paid_till_now_vat_inc'),
              render: ({ record }) => (
                <LocalizedMonetaryAmount
                  amount={convertMonetaryIntToDecimal(
                    record.transactions.reduce((acc, { paid_amount }) => acc + paid_amount, 0),
                    record.currency,
                  )}
                  currency={record.currency}
                />
              ),
            },
            {
              id: '6',
              name: t('payout-reports.amount_left_to_be_paid_vat_inc'),
              render: ({ record }) => (
                <LocalizedMonetaryAmount
                  amount={
                    convertMonetaryIntToDecimal(record.amount, record.currency) -
                    convertMonetaryIntToDecimal(
                      record.transactions.reduce((acc, { paid_amount }) => acc + paid_amount, 0),
                      record.currency,
                    )
                  }
                  currency={record.currency}
                />
              ),
            },

            {
              id: '7',
              name: '',
              render: ({ record }) => (
                <ActionsContainer>
                  {record.invoice?.invoice_id && (
                    <ActionsMenuItem
                      onClick={() => {
                        handleProductSaleInvoiceDownload(record.invoice?.invoice_id);
                      }}
                    >
                      <FormattedMessage id="payout-reports.download-pdf" />
                    </ActionsMenuItem>
                  )}
                </ActionsContainer>
              ),
            },
          ]}
        />
      )}
    </div>
  );
};

export default InvoiceList;
